import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import { brandIdentity } from "../data/brand";

const topics = [
  "Editorial tip or correction",
  "Brand partnership or sponsorship",
  "Robot launch or event coverage",
  "Copyright or attribution request",
  "Privacy request",
  "Website support",
  "Other",
];

export default function Contact() {
  const location = useLocation();
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Contact", to: "/contact" },
  ];
  const [form, setForm] = useState({ name: "", email: "", topic: topics[0], company: "", website: "", timing: "", budget: "", message: "", consent: false });
  const [status, setStatus] = useState("");
  const commercial = form.topic === topics[1] || form.topic === topics[2];

  useEffect(() => {
    const requestedTopic = new URLSearchParams(location.search).get("topic");
    const topic = { partnership: topics[1], launch: topics[2] }[requestedTopic];
    if (topic) setForm((current) => ({ ...current, topic }));
  }, [location.search]);

  const mailBody = useMemo(() => [
    `Name ${form.name.trim()}`,
    `Reply email ${form.email.trim()}`,
    `Topic ${form.topic}`,
    ...(commercial ? [
      `Company or project ${form.company.trim() || "Not provided"}`,
      `Product link ${form.website.trim() || "Not provided"}`,
      `Launch window ${form.timing.trim() || "To discuss"}`,
      `Budget range ${form.budget.trim() || "To discuss"}`,
    ] : []),
    "",
    form.message.trim(),
    "",
    "Sent from the TechniaHQRobot contact page.",
  ].join("\n"), [form, commercial]);

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    if (status) setStatus("");
  };

  const submitForm = (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim() || !form.consent) {
      setStatus("Complete the required fields and confirm the privacy notice.");
      return;
    }
    const subject = `[TechniaHQRobot] ${form.topic}`;
    const mailto = `mailto:${brandIdentity.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailBody)}`;
    setStatus("Your email draft is opening. Attach files in your email application before sending.");
    window.location.href = mailto;
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(brandIdentity.email);
      setStatus("Email address copied.");
    } catch {
      setStatus(`Copy this address ${brandIdentity.email}`);
    }
  };

  const copyMessage = async (event) => {
    if (!event.currentTarget.form.reportValidity()) return;
    try {
      await navigator.clipboard.writeText(mailBody);
      setStatus("Message copied. Paste it into an email and send it to " + brandIdentity.email + ".");
    } catch {
      setStatus("Copy your message from the fields below and email it to " + brandIdentity.email + ".");
    }
  };

  return (
    <>
      <SEO
        title="Contact TechniaHQRobot for Robotics Launches and Partnerships"
        description="Contact TechniaHQRobot about robotics news, corrections, sponsorships, launch coverage, copyright, privacy or website support."
        path="/contact"
        schema={buildBreadcrumbSchema(breadcrumbs)}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Contact"
        title="Contact TechniaHQRobot"
        description="Send a robotics tip, correction, partnership request, copyright notice, privacy request or website question."
      />

      <div className="site-shell py-10 sm:py-14">
        <div className="grid gap-7 lg:grid-cols-[minmax(0,1.15fr)_minmax(300px,0.85fr)] lg:items-start">
          <section className="article-panel">
            <div className="max-w-3xl">
              <p className="text-sm leading-7 text-neutral-600">
                Complete the form to prepare an email. Send it from your email app, or copy the message and email it to us directly. Opening a draft does not send your message.
              </p>
            </div>

            <form className="mt-8 grid gap-5" onSubmit={submitForm}>
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-black text-neutral-900">
                  Name <span className="text-red-600" aria-hidden="true">*</span>
                  <input
                    name="name"
                    value={form.name}
                    onChange={updateField}
                    autoComplete="name"
                    required
                    className="rounded-2xl border border-neutral-300 bg-white px-4 py-3 font-medium outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="Your name"
                  />
                </label>
                <label className="grid gap-2 text-sm font-black text-neutral-900">
                  Email <span className="text-red-600" aria-hidden="true">*</span>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={updateField}
                    autoComplete="email"
                    required
                    className="rounded-2xl border border-neutral-300 bg-white px-4 py-3 font-medium outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    placeholder="name@example.com"
                  />
                </label>
              </div>

              <label className="grid gap-2 text-sm font-black text-neutral-900">
                Topic
                <select
                  name="topic"
                  value={form.topic}
                  onChange={updateField}
                  className="rounded-2xl border border-neutral-300 bg-white px-4 py-3 font-medium outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                >
                  {topics.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                </select>
              </label>

              {commercial && (
                <fieldset className="grid gap-4 rounded-2xl border border-blue-100 bg-blue-50 p-4 sm:grid-cols-2">
                  <legend className="px-2 text-sm font-black text-neutral-950">Campaign details, optional</legend>
                  {[
                    ["company", "Company or project", "Your organization or product"],
                    ["website", "Product or announcement link", "https://example.com/product"],
                    ["timing", "Launch window", "Publication date or campaign period"],
                    ["budget", "Budget range", "Amount and currency, or to discuss"],
                  ].map(([name, label, placeholder]) => (
                    <label key={name} className="grid gap-2 text-sm font-bold text-neutral-900">
                      {label}
                      <input name={name} value={form[name]} onChange={updateField} placeholder={placeholder} className="min-w-0 rounded-xl border border-neutral-300 bg-white px-3 py-3 font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                    </label>
                  ))}
                </fieldset>
              )}

              <label className="grid gap-2 text-sm font-black text-neutral-900">
                {commercial ? "Campaign brief" : "Message"} <span className="text-red-600" aria-hidden="true">*</span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={updateField}
                  required
                  rows={9}
                  className="resize-y rounded-2xl border border-neutral-300 bg-white px-4 py-3 font-medium leading-7 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder={commercial ? "Describe the product, audience, preferred formats and the action you want readers to take. Add source links, available footage, usage rights and any embargo." : "Include the relevant URL, robot or company name, source link, deadline and the action you are requesting."}
                />
              </label>

              <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-sm leading-6 text-neutral-700">
                <input
                  name="consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={updateField}
                  required
                  className="mt-1 h-4 w-4 rounded border-neutral-300"
                />
                <span>
                  I understand that submitting opens my email application and that the message will be handled according to the <a className="font-black text-blue-700 underline underline-offset-4" href="/privacy-policy">Privacy Policy</a>.
                </span>
              </label>

              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" className="rounded-full bg-blue-600 px-6 py-3 text-sm font-black text-white transition hover:bg-blue-700">
                  Open email draft
                </button>
                <button type="button" onClick={copyAddress} className="rounded-full border border-neutral-300 bg-white px-6 py-3 text-sm font-black text-neutral-800 transition hover:border-blue-300 hover:text-blue-700">
                  Copy email address
                </button>
                <button type="button" onClick={copyMessage} className="rounded-full border border-neutral-300 bg-white px-6 py-3 text-sm font-black text-neutral-800 hover:border-blue-300 hover:text-blue-700">Copy message</button>
              </div>
              <p className="min-h-6 text-sm font-bold text-blue-800" role="status" aria-live="polite">{status}</p>
            </form>
          </section>

          <aside className="grid gap-5 lg:sticky lg:top-24">
            <section className="rounded-[1.75rem] border border-blue-100 bg-blue-50 p-6">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Direct email</p>
              <a href={`mailto:${brandIdentity.email}`} className="mt-3 block break-all text-lg font-black text-neutral-950 underline underline-offset-4">
                {brandIdentity.email}
              </a>
              <p className="mt-4 text-sm leading-7 text-neutral-700">
                For corrections, include the page URL and the primary source supporting the change. For partnerships, include the company, deliverable, timing and budget range.
              </p>
            </section>

            <section className="soft-card p-6">
              <h2 className="text-xl font-black text-neutral-950">Best contact method</h2>
              <p className="mt-3 text-sm leading-7 text-neutral-600">
                Email is the official channel for legal, privacy, copyright and commercial requests. Include the relevant page URL so the request can be checked against the published article.
              </p>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}
